import { InjectionToken, Signal } from '@angular/core';

export type BaseMapProvider = 'inventa' | 'esri';

export type InventaMapType = 'satellite' | 'terrain' | 'roadmap' | 'hybrid';

/**
 * A complete base-map selection. This is the single entry point for base-map
 * changes: implementations own every state transition (provider switch,
 * esri tile mount/unmount, Inventa (Google) layer swap).
 */
export type BasemapSelection =
  | { provider: 'inventa'; mapType: InventaMapType }
  | { provider: 'esri'; tile: string };

export interface ExternalMapLayer {
  key: string;
  label: string;
  checked: boolean;
  /** Percent transparency (0–100) applied to the layer on the map. */
  transparency?: number;
}

export interface ExternalMapBaseTile {
  key: string;
  label: string;
}

/** One legend swatch rendered by the ArcGIS service (base64 PNG + class label). */
export interface ExternalLayerLegendEntry {
  label?: string;
  imageData?: string;
  contentType?: string;
  width?: number;
  height?: number;
}

/** Legend of one feature layer plus its drawingInfo transparency (0–100). */
export interface ExternalLayerLegend {
  entries: ExternalLayerLegendEntry[];
  transparency?: number;
}

export abstract class ExternalMapLayersService {
  abstract readonly layers: Signal<ExternalMapLayer[]>;
  abstract readonly baseTileOptions: Signal<ExternalMapBaseTile[]>;
  abstract readonly activeBaseTileKey: Signal<string | undefined>;
  readonly authenticated?: Signal<boolean>;
  /** ArcGIS account name of the signed-in user, when authenticated. */
  readonly username?: Signal<string | undefined>;
  readonly preferredBaseTileKey?: Signal<string | undefined>;
  readonly available?: Signal<boolean>;
  readonly canAddLayers?: Signal<boolean>;
  readonly featureLayersActive?: Signal<boolean>;
  readonly provider?: Signal<BaseMapProvider>;
  abstract toggle(key: string, checked: boolean): void;
  /**
   * Applies a complete base-map selection: mounts/unmounts the esri tile and
   * switches the Inventa (Google) map type, keeping provider and map in sync.
   */
  abstract setBasemap(target: BasemapSelection): void;
  abstract setMap(map: L.Map): void;
  /**
   * Releases the map instance a base-map component attached. Passing the
   * destroyed instance lets implementations keep the shared state intact
   * when another live map has taken ownership since.
   */
  abstract reset(map?: L.Map): void;
  abstract logout(): void;
  login?(): void;
  addLayers?(): void;
  removeLayer?(key: string): void;
  focusLayer?(key: string): void;
  /** Legend swatches and drawingInfo transparency of a feature layer. */
  legend?(key: string): Promise<ExternalLayerLegend>;
  /** Applies the given transparency (0–100) to the layer on the map and persists it. */
  setLayerTransparency?(key: string, transparency: number): void;
  setProvider?(provider: BaseMapProvider): void;
}

export const EXTERNAL_MAP_LAYERS_SERVICE = new InjectionToken<ExternalMapLayersService | undefined>(
  'EXTERNAL_MAP_LAYERS_SERVICE',
);
